//
// Lesson store
//
// Stores and manages all lessons that can be administered by the
// authenticated user.
//

import { normalize } from 'normalizr';
import * as client from 'api/client';
import { lesson as lessonSchema } from 'api/schema';
import { LOGOUT, getToken } from './auth';
import { RECEIVE_ENTITIES } from './user';
import { PIN_CREATED, PIN_DELETED } from './pins';
import { importMap, resetMap } from './map';
import { showSuccess, showError } from './messages';

// ------------------------------------
// Action definitions
// ------------------------------------

export const OPEN_LESSON    = 'LESSONS/OPEN';
export const CLOSE_LESSON   = 'LESSONS/CLOSE';
export const CREATE_LESSON  = 'LESSONS/CREATE';
export const LESSON_CREATED = 'LESSONS/CREATE_OK';
export const UPDATE_LESSON  = 'LESSONS/UPDATE';
export const LESSON_UPDATED = 'LESSONS/UPDATE_OK';
export const DELETE_LESSON  = 'LESSONS/DELETE';
export const LESSON_DELETED = 'LESSONS/DELETE_OK';
export const ERROR          = 'LESSONS/ERROR';

// ------------------------------------
// Actions creators
// ------------------------------------

export const openLesson = id => (dispatch, getState) => {
  const state = getState();
  const lesson = getLesson(state, id);

  dispatch({ type: OPEN_LESSON, id });
  dispatch(importMap(lesson.data));
};

export const closeLesson = () => dispatch => {
  dispatch({ type: CLOSE_LESSON });
  dispatch(resetMap());
};

export const createLesson = (name, templateId) => (dispatch, getState) => {
  dispatch({ type: CREATE_LESSON });

  const state = getState();
  const token = getToken(state);

  const classId = state.classes.selected;
  const data = {
    name,
    template_id: templateId,
    school_class_id: classId,
    data: null
  };

  return client.post(`/api/lessons`, data, token)
    .then(lesson => {
      dispatch({ type: LESSON_CREATED, lesson });
      dispatch(openLesson(lesson.id));
      dispatch(showSuccess('lesson.create.success'));
    })
    .catch(error => {
      dispatch(fail());
      dispatch(showError('lesson.create.failed'));
    });
};

export const updateLesson = () => (dispatch, getState) => {
  dispatch({ type: UPDATE_LESSON });

  const state = getState();
  const token = getToken(state);
  const id = state.lessons.selected;

  const data = {
    data: state.map
  };

  return client.put(`/api/lessons/${id}`, data, token)
    .then(json => {
      const { entities } = normalize(json, lessonSchema);
      const lesson = entities.lessons[id];
      dispatch({ type: LESSON_UPDATED, lesson });
      dispatch(showSuccess('lesson.update.success'));
    })
    .catch(error => {
      dispatch(fail());
      dispatch(showError('lesson.update.failed'));
    });
};

export const deleteLesson = (id) => (dispatch, getState) => {
  dispatch({ type: DELETE_LESSON });

  const state = getState();
  const token = getToken(state);

  return client.del(`/api/lessons/${id}`, token)
    .then(() => {
      dispatch({ type: LESSON_DELETED, id });
      dispatch(showSuccess('lesson.delete.success'));
    })
    .catch(error => {
      dispatch(fail());
      dispatch(showError('lesson.delete.failed'));
    });
};

export const fail = () => ({ type: ERROR });

// ------------------------------------
// Initial state
// ------------------------------------

const initialState = {
  items: {},
  selected: null,
  pending: false
};

// ------------------------------------
// Reducer
// ------------------------------------

export default (state = initialState, action) => {
  switch (action.type) {

    // Populate store with received user data.
    case RECEIVE_ENTITIES: {
      const items = action.entities.lessons || {};
      return { ...state, items };
    }

    // Set selected lesson.
    case OPEN_LESSON: {
      return { ...state, selected: action.id };
    }

    // Clear selected lesson.
    case CLOSE_LESSON: {
      return { ...state, selected: null };
    }

    // CRUD request is pending.
    case CREATE_LESSON:
    case UPDATE_LESSON:
    case DELETE_LESSON: {
      return { ...state, pending: true };
    }

    // Update store with new/updated lesson.
    case LESSON_CREATED:
    case LESSON_UPDATED: {
      const { lesson } = action;
      const items = { ...state.items, [`${lesson.id}`]: lesson };

      return {
        ...state,
        items,
        selected: lesson.id,
        pending: false
      };
    }

    // Remove deleted lesson from the store.
    case LESSON_DELETED: {
      const items = { ...state.items };
      const selected = state.selected === action.id ? null : state.selected;
      delete items[action.id];

      return {
        ...state,
        items,
        selected,
        pending: false
      };
    }

    // Add new pin to the lesson it belongs to (if applicable).
    case PIN_CREATED: {
      const { pin } = action;
      const items = { ...state.items };
      const lesson = items[pin.lesson_id];

      if (lesson) {
        items[lesson.id] = Object.assign({}, lesson, {
          pins: [ ...lesson.pins, pin.id ]
        });

        return { ...state, items };
      }

      return state;
    }

    // Delete pin from lesson to which it belonged.
    case PIN_DELETED: {
      const items = { ...state.items };
      const lesson = Object.values(items).find(lesson =>
        lesson.pins.includes(action.id)
      );

      items[lesson.id] = Object.assign({}, lesson, {
        pins: lesson.pins.filter(id => id !== action.id)
      });

      return { ...state, items };
    }

    // API request failed.
    case ERROR:
      return { ...state, pending: false };

    // Reset store on logout.
    case LOGOUT:
      return initialState;

    default:
      return state
  }
};

// ------------------------------------
// Utils
// ------------------------------------

export const getAllLessons = state => {
  return Object.values(state.lessons.items);
};

export const getLessons = (state, ids) => {
  const { items } = state.lessons;
  return ids.map(id => ({ ...items[id] }));
};

export const getLesson = (state, id) => {
  const { items } = state.lessons;
  return items.hasOwnProperty(id)
    ? { ...items[id] }
    : null;
};

export const getSelectedLesson = state => {
  const { items, selected } = state.lessons;
  return selected
    ? { ...items[selected] }
    : null;
};

// NOTE: Denormalized example
// export const getLessons = (state, ids) => {
//   const entities = getEntities(state, [
//     'lessons',
//     'pins',
//     'templates'
//   ]);
//
//   return denormalize(ids, [ lesson ], entities);
// }



// WEBPACK FOOTER //
// ./src/store/modules/lessons.js